import {
  convertDatesForFilterPayload,
  reportTransformTimeForAPI
} from "./commonReportHelpers";
import { convertToNormalTime } from "../reportCommonHelpers";
import { DateTime } from "luxon";
import { StationaryReportResponse } from "app/data/types/reportsTypes";

const STATIONARY_REPORT_TYPE = 10;

const transformPayloadValues = (values: any) => ({
  daysOfWeek:
    values.daysOfWeek?.length === 7 ? [] : values.daysOfWeek?.map(Number),
  rideModes: values?.rideModes?.map(Number) || []
});

const transformDateTimeValues = (values: any, dateValues: any) => {
  const timeValues = values.daysOfWeek
    ? {
        startTime: reportTransformTimeForAPI(
          values.startTime + ":00",
          dateValues.startDate
        ),
        endTime: reportTransformTimeForAPI(
          values.endTime + ":00",
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      }
    : {};

  return {
    ...values,
    ...timeValues
  };
};

const createBasePayload = (
  dateValues: any,
  values: any,
  apiPayload: any,
  reportType: number
) => ({
  ...(apiPayload ?? {}),
  startDate: dateValues.startDate,
  endDate: dateValues.endDate,
  ...transformDateTimeValues(values, dateValues),
  reportType,
  ...transformPayloadValues(values)
});

export const buildStationaryReportPayload = async (
  apiPayload: any,
  values: any,
  dateValues: any
) => {
  const { formattedStartDate, formattedEndDate } =
    convertDatesForFilterPayload(dateValues);

  return createBasePayload(
    { startDate: formattedStartDate, endDate: formattedEndDate },
    values,
    apiPayload,
    STATIONARY_REPORT_TYPE
  );
};

export const stationaryReportsDataFormatter = (
  originalData: StationaryReportResponse
) => {
  if (!originalData?.length) return [];

  const byName = new Map<
    string,
    {
      name: string;
      uniqueId?: string;
      serialNumber?: string;
      records: any[];
    }
  >();

  originalData.forEach((entry: any) => {
    const details = entry.detailedInfo || [];
    if (!details.length) return;

    const detailsByName = new Map<string, any[]>();
    details.forEach((detail: any) => {
      const key = detail?.name || "N/A";
      if (!detailsByName.has(key)) detailsByName.set(key, []);
      detailsByName.get(key)!.push(detail);
    });

    detailsByName.forEach((rides, name) => {
      const sorted = [...rides].sort(
        (a: any, b: any) =>
          new Date(a.start).getTime() - new Date(b.start).getTime()
      );

      const dateLabel = DateTime.fromISO(entry.date).toFormat("dd-MMM-yyyy");
      const earliest = DateTime.fromISO(sorted[0].start);
      const latest = DateTime.fromISO(sorted[sorted.length - 1].end);
      const timeRange = `${earliest.toFormat("H:mm")} – ${latest.toFormat("H:mm")}`;

      let group = byName.get(name);
      if (!group) {
        const first = sorted[0];
        group = {
          name,
          uniqueId: first?.uniqueId || first?.objectId,
          serialNumber: first?.serialNumber,
          records: []
        };
        byName.set(name, group);
      } else {
        if (!group.uniqueId) {
          group.uniqueId = sorted[0]?.uniqueId || sorted[0]?.objectId;
        }
        if (!group.serialNumber) {
          group.serialNumber = sorted[0]?.serialNumber;
        }
      }

      group.records.push({
        label: `${dateLabel}; ${timeRange}`,
        count: sorted.length,
        data: sorted
      });
    });
  });

  const result = Array.from(byName.values());

  result.forEach((group) => {
    group.records.sort((a, b) => {
      const dateA = a.data[0]?.start || "";
      const dateB = b.data[0]?.start || "";
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  });

  result.sort((a, b) => a.name.localeCompare(b.name));

  return result;
};

export const handleStationaryDateRange = (
  filterValues: any,
  dateValues: any,
  apiPayload: any
) => {
  const startDate = convertToNormalTime(dateValues.startDate);
  const endDate = convertToNormalTime(dateValues.endDate, true);

  return createBasePayload(
    { startDate, endDate },
    filterValues,
    apiPayload,
    STATIONARY_REPORT_TYPE
  );
};
